import React, {ReactNode, useMemo} from "react";
import {FormattedMessage} from "react-intl";
import {Badge, Stats} from "peggirkit";
import StatItem from "peggirkit/dist/components/Stats/StatsElements/StatItem";
import {CartInTimeline} from "@widgets/reports/cartTimeline/CartTimelineUtils";
import {
    cartTimelineFigures,
    CartTimelineFigures,
    formatMinutesSeconds
} from "@widgets/reports/cartTimeline/stats/CartTimelineFigures";
import {formatMoney} from "@/utils/MoneyUtils";

type Props = {
    carts: CartInTimeline[],
};

/** A small gray badge with extra information below a figure. */
const SecondaryIndication = ({children}: { children: ReactNode }) => (
    <Badge color={"secondary"} size={"xs"}>
        {children}
    </Badge>
);

/**
 * Key figures of the carts of the ordering site (POS orders left out).
 */
const CartTimelineStats = ({carts}: Props) => {
    const figures: CartTimelineFigures = useMemo(() => cartTimelineFigures(carts), [carts]);

    return (
        <Stats
            size={"xs"}
            itemsPerRow={1}
            breakpoints={{
                sm: {
                    itemsPerRow: 2,
                },
                xl: {
                    size: "sm",
                    itemsPerRow: 4,
                },
            }}
        >
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.active"}/>}
                value={figures.active}
                indication={
                    figures.paying > 0
                        ? (
                            <Badge color={"warning"} size={"xs"}>
                                <FormattedMessage id={"cartTimeline.stats.active.paying"} values={{n: figures.paying}}/>
                            </Badge>
                        )
                        : undefined
                }
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.started"}/>}
                value={figures.started}
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.ordered"}/>}
                value={figures.ordered}
                indication={
                    figures.conversion !== null
                        ? (
                            <Badge color={"success"} size={"xs"}>
                                <FormattedMessage
                                    id={"cartTimeline.stats.ordered.conversion"}
                                    values={{percentage: figures.conversion}}
                                />
                            </Badge>
                        )
                        : undefined
                }
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.abandoned"}/>}
                value={figures.abandoned}
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.timeToOrder"}/>}
                value={figures.averageSecondsToOrder === null ? "–" : formatMinutesSeconds(figures.averageSecondsToOrder)}
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.account"}/>}
                value={figures.accountShare === null ? "–" : `${figures.accountShare}%`}
                indication={
                    figures.accountShare !== null
                        ? (
                            <SecondaryIndication>
                                <FormattedMessage
                                    id={"cartTimeline.stats.account.counts"}
                                    values={{account: figures.withAccount, guest: figures.withoutAccount}}
                                />
                            </SecondaryIndication>
                        )
                        : undefined
                }
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.delivery"}/>}
                value={figures.deliveryShare === null ? "–" : `${figures.deliveryShare}%`}
                indication={
                    figures.deliveryShare !== null
                        ? (
                            <SecondaryIndication>
                                <FormattedMessage
                                    id={"cartTimeline.stats.delivery.counts"}
                                    values={{delivery: figures.delivery, takeaway: figures.takeaway}}
                                />
                            </SecondaryIndication>
                        )
                        : undefined
                }
            />
            <StatItem
                name={<FormattedMessage id={"cartTimeline.stats.averageOrderValue"}/>}
                value={figures.averageOrderValue === null ? "–" : formatMoney(figures.averageOrderValue)}
                indication={
                    figures.averageOrderValue !== null
                        ? (
                            <SecondaryIndication>
                                <FormattedMessage
                                    id={"cartTimeline.stats.averageOrderValue.revenue"}
                                    values={{amount: formatMoney(figures.revenue)}}
                                />
                            </SecondaryIndication>
                        )
                        : undefined
                }
            />
        </Stats>
    );
};

export default CartTimelineStats;